"use client";

/**
 * ComponentLabelsOverlay
 *
 * When enabled, renders a small pill label at the top-left corner of every
 * rendered component's first DOM instance. Designed to be toggled on/off via
 * the "Labels" button in RenderedComponentsSection.
 *
 * Dev-only — portal-rendered, pointer-events: none, z-index 2147483643.
 * Scroll/resize positions are recalculated via RAF-throttled listeners.
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useIsMounted } from "../hooks/use-is-mounted";
import {
  fiberToDOM,
  type RenderedComponentWithFibers,
} from "../utils/fiber-dom";
import type {
  ComponentLabelsOverlayProps,
  LabelPosition,
} from "./component-labels-overlay.types";

export function ComponentLabelsOverlay({
  components,
}: ComponentLabelsOverlayProps) {
  const [positions, setPositions] = useState<LabelPosition[]>([]);
  const rafIdRef = useRef<number | null>(null);
  const mounted = useIsMounted();
  // Always holds the latest components so the stable scroll/resize handler
  // never closes over a stale prop value.
  const componentsRef = useRef(components);
  useEffect(() => {
    componentsRef.current = components;
  }, [components]);

  const recalcPositions = (
    currentComponents: RenderedComponentWithFibers[],
  ) => {
    const next: LabelPosition[] = [];
    const vh = window.innerHeight;
    const vw = window.innerWidth;

    for (const comp of currentComponents) {
      // Use only the first fiber instance for the label position
      const fiber: unknown = comp.fibers[0];
      if (!fiber) continue;
      const el = fiberToDOM(fiber as import("../utils/fiber-dom").Fiber);
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      // Skip elements that are completely outside the viewport
      if (rect.bottom < 0 || rect.top > vh || rect.right < 0 || rect.left > vw)
        continue;
      if (rect.width === 0 && rect.height === 0) continue;
      next.push({ name: comp.name, top: rect.top, left: rect.left });
    }

    setPositions(next);
  };

  // Initial calculation and recalc on components change
  useEffect(() => {
    if (!mounted) return;
    const frame = requestAnimationFrame(() => recalcPositions(components));
    return () => cancelAnimationFrame(frame);
  }, [mounted, components]);

  // Scroll and resize listeners (RAF-throttled).
  // Uses componentsRef so the stable handler always reads the latest components
  // list — avoids the stale-closure bug where filtering the list wouldn't
  // be reflected in scroll-triggered label recalculations.
  useEffect(() => {
    if (!mounted) return;

    const scheduleRecalc = () => {
      if (rafIdRef.current !== null) return;
      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = null;
        recalcPositions(componentsRef.current);
      });
    };

    window.addEventListener("scroll", scheduleRecalc, {
      capture: true,
      passive: true,
    });
    window.addEventListener("resize", scheduleRecalc, { passive: true });

    return () => {
      window.removeEventListener("scroll", scheduleRecalc, { capture: true });
      window.removeEventListener("resize", scheduleRecalc);
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, [mounted]);

  if (!mounted || typeof document === "undefined") return null;

  return createPortal(
    <div
      data-feedback-capture-exclude
      style={{
        position: "fixed",
        inset: 0,
        pointerEvents: "none",
        overflow: "hidden",
        zIndex: 2147483643,
      }}
    >
      {positions.map((pos, i) => (
        <span
          key={i}
          style={{
            position: "absolute",
            top: pos.top,
            left: pos.left,
            background: "rgba(0,0,0,0.78)",
            color: "#fff",
            fontSize: 10,
            lineHeight: "16px",
            padding: "0 5px",
            borderRadius: "0 0 4px 0",
            fontFamily: "monospace",
            whiteSpace: "nowrap",
            maxWidth: 220,
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "inline-block",
          }}
        >
          {pos.name}
        </span>
      ))}
    </div>,
    document.body,
  );
}
